import {
  Button,
  CardHeader,
  CardTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  PiPalette,
  PiEyeSlash,
  PiPushPin,
  PiPushPinSlash,
} from 'react-icons/pi';
import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { useMapLegendColors } from '@ais-ui/websockets';

import { cn } from '@ais-ui/utils';

import { useMapLegendSettings } from './ColorBy/useMapLegendSettings';
import { useLayers } from '@ais-ui/react-openlayers';
import { ColourByOptions } from './ColorBy/ColourByOptions';
import { ColorByShipType } from './ColorBy/ColorByShipType';
import { ColorBySourceType } from './ColorBy/ColorBySourceType';
import { ColorByTransponderType } from './ColorBy/ColorByTransponderType';
import { ColorByShipList } from './ColorBy/ColorByShipList';
import { ColorByTargetAge, UpdateTargetAge } from './ColorBy/ColorByTargetAge';
import { useLocalization } from '@ais-ui/locale-management';
import { useColorByPin } from './ColorByPinContext';

const noop = () => {};

interface ShipsLegendProps {
  className?: string;
}

type VisibilityState = 'all' | 'some' | 'none';

export const ColorTargetsBy = React.memo(({ className }: ShipsLegendProps) => {
  const [open, setOpen] = useState(false);
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { data: legendColors } = useMapLegendColors();
  const { isPinned, togglePin } = useColorByPin();

  const [numIntervals, setNumIntervals] = useState(
    settings['ribbonPlugin.settings']['legend_age_intervals'] ?? 2,
  );
  const [intervalSize, setIntervalSize] = useState(
    settings['ribbonPlugin.settings']['legend_age_intervalsize'] ?? 15,
  );

  const { onDisableAll, onEnableAll } = useMapLegendSettings();
  const { layers, toggleLayerVisibility } = useLayers();

  const shipLayer = useMemo(
    () => layers.find((l) => l.name.toLowerCase() === 'vessels'),
    [layers],
  );

  const hideShowAllText = useMemo(
    () =>
      !shipLayer ? 'Show All' : shipLayer.isVisible ? 'Hide All' : 'Show All',
    [shipLayer],
  );

  const visibilityState = useMemo((): VisibilityState => {
    const colorState = settings['ribbonPlugin.settings']?.['ship_color_state'];

    // No indicator needed when no categorization is active
    if (
      !colorState ||
      colorState === 'no colors' ||
      colorState === 'LegendFilter'
    ) {
      return 'all';
    }

    if (!shipLayer?.isVisible) {
      return 'none';
    }

    const hiddenItems =
      settings['ribbonPlugin.settings']?.['ship_color_noshow_sel'] || [];
    const totalItems = legendColors?.length || 0;

    // No items to hide/show
    if (totalItems === 0) {
      return 'all';
    }

    // Count how many of the current legend items are hidden
    const currentLegendNames = legendColors?.map((item) => item.name) || [];
    const hiddenCount = hiddenItems.filter((item: string) =>
      currentLegendNames.includes(item),
    ).length;

    if (hiddenCount === 0) {
      return 'all';
    }
    if (hiddenCount >= totalItems) {
      return 'none';
    }
    return 'some';
  }, [shipLayer?.isVisible, settings, legendColors]);

  const colorStateChecks = useMemo(
    () => ({
      isShiplist:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendShipList',
      isTransponderType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendTransponderType',
      isShipType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendShipType',
      isSourceType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendSourceType',
      isTargetAge:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendAge',
    }),
    [settings],
  );

  const toggleVisibility = React.useCallback(() => {
    if (shipLayer) {
      if (shipLayer.isVisible) {
        onDisableAll();
        toggleLayerVisibility(shipLayer.layer, false);
      } else {
        onEnableAll();
        toggleLayerVisibility(shipLayer.layer, true);
      }
    }
  }, [shipLayer, onDisableAll, onEnableAll, toggleLayerVisibility]);

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                id="color-targets-by-tool-button"
                size="icon"
                variant="outline"
                className={cn(
                  'flex items-center justify-center size-8 p-1 group/btn rounded-lg relative',
                  className,
                )}
                onClick={() => setOpen((prev) => !prev)}
              >
                <PiPalette className="size-5 group-hover/btn:text-primary" />
                {visibilityState === 'some' && (
                  <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center px-0.5 py-0.5 text-xs font-bold leading-none bg-muted-foreground rounded-full">
                    <PiEyeSlash className="size-3 text-background" />
                  </span>
                )}
                {visibilityState === 'none' && (
                  <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center px-0.5 py-0.5 text-xs font-bold leading-none bg-primary rounded-full">
                    <PiEyeSlash className="size-3 text-background" />
                  </span>
                )}
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side="right" sideOffset={4}>
            {messages.mapColorByButtonTooltip}
          </TooltipContent>
          <PopoverContent
            side="right"
            align="end"
            className="bg-background w-fit shadow-sm"
          >
            <CardHeader className="p-2 space-y-6">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg font-medium">Color By</CardTitle>
                <div className="flex items-center -mt-5 -mr-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={togglePin}
                        className={cn(isPinned && 'text-primary')}
                      >
                        {isPinned ? (
                          <PiPushPinSlash className="size-4" />
                        ) : (
                          <PiPushPin className="size-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isPinned ? 'Unpin from legends' : 'Pin to legends'}
                    </TooltipContent>
                  </Tooltip>
                  <Button
                    id="close-color-targets-by-tool-button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setOpen(false)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between space-x-36">
                <ColourByOptions />
                <div className="flex">
                  {colorStateChecks.isTargetAge && (
                    <UpdateTargetAge
                      intervalSize={intervalSize}
                      numIntervals={numIntervals}
                    />
                  )}
                  <Button variant="ghost" onClick={toggleVisibility}>
                    {hideShowAllText}
                  </Button>
                </div>
              </div>
              {colorStateChecks.isShipType && <ColorByShipType />}
              {colorStateChecks.isSourceType && <ColorBySourceType />}
              {colorStateChecks.isTransponderType && <ColorByTransponderType />}
              {colorStateChecks.isShiplist && <ColorByShipList />}
              {colorStateChecks.isTargetAge && (
                <ColorByTargetAge
                  numIntervals={numIntervals}
                  intervalSize={intervalSize}
                  setIntervalSize={setIntervalSize}
                  setNumIntervals={setNumIntervals}
                />
              )}
            </div>
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
});
